import React, { forwardRef } from "react";
import { TextInput as RNTextInput } from "react-native";
import { InputPrimitiveProps } from "@xsolla/xui-primitives-core";

// Map web input types to React Native keyboard types
const keyboardTypeMap: Record<string, any> = {
  text: "default",
  number: "numeric",
  email: "email-address",
  tel: "phone-pad",
  url: "url",
  decimal: "decimal-pad",
};

// Map web inputMode to React Native keyboard types
const inputModeToKeyboardType: Record<string, any> = {
  none: "default",
  text: "default",
  decimal: "decimal-pad",
  numeric: "number-pad",
  tel: "phone-pad",
  search: "default",
  email: "email-address",
  url: "url",
};

// Map web autoComplete to React Native textContentType (iOS)
const autoCompleteToTextContentType: Record<string, any> = {
  "one-time-code": "oneTimeCode",
  email: "emailAddress",
  username: "username",
  password: "password",
  "new-password": "newPassword",
  tel: "telephoneNumber",
  "postal-code": "postalCode",
  name: "name",
};

export const InputPrimitive = forwardRef<RNTextInput, InputPrimitiveProps>(
  (
    {
      value,
      placeholder,
      onChange,
      onChangeText,
      onFocus,
      onBlur,
      onKeyDown,
      disabled,
      secureTextEntry,
      style,
      color,
      fontSize,
      fontFamily,
      placeholderTextColor,
      maxLength,
      type,
      inputMode,
      autoComplete,
      id,
      "aria-describedby": ariaDescribedBy,
      "aria-label": ariaLabel,
      "aria-disabled": ariaDisabled,
      "data-testid": dataTestId,
      testID,
    },
    ref
  ) => {
    const handleChangeText = (text: string) => {
      onChangeText?.(text);

      // Create a synthetic event for onChange compatibility
      // Include nativeEvent and no-op methods to prevent runtime errors
      // when consumers expect DOM-like event behavior
      if (onChange) {
        const syntheticEvent = {
          target: { value: text },
          currentTarget: { value: text },
          type: "change",
          nativeEvent: { text },
          preventDefault: () => {},
          stopPropagation: () => {},
          isTrusted: false,
        } as unknown as React.ChangeEvent<HTMLInputElement>;
        onChange(syntheticEvent);
      }
    };

    // Determine keyboard type - inputMode takes precedence over type
    const keyboardType = inputMode
      ? inputModeToKeyboardType[inputMode] || "default"
      : type
        ? keyboardTypeMap[type] || "default"
        : "default";

    // Determine textContentType for iOS autofill
    const textContentType = autoComplete
      ? autoCompleteToTextContentType[autoComplete]
      : undefined;

    let resolvedFontFamily = fontFamily
      ? fontFamily.split(",")[0].replace(/['"]/g, "").trim()
      : undefined;

    if (
      resolvedFontFamily === "Pilat Wide" ||
      resolvedFontFamily === "Pilat Wide Bold" ||
      resolvedFontFamily === "Aktiv Grotesk"
    ) {
      resolvedFontFamily = undefined;
    }

    return (
      <RNTextInput
        ref={ref}
        value={value}
        placeholder={placeholder}
        onChangeText={handleChangeText}
        onFocus={onFocus}
        onBlur={onBlur}
        onKeyPress={(e) => {
          // Map onKeyPress to onKeyDown for cross-platform compatibility
          // Include preventDefault to avoid runtime errors when consumers call it
          if (onKeyDown) {
            onKeyDown({
              key: e.nativeEvent.key,
              preventDefault: () => {},
            } as any);
          }
        }}
        editable={!disabled}
        secureTextEntry={secureTextEntry || type === "password"}
        keyboardType={keyboardType}
        textContentType={textContentType}
        style={[
          {
            color,
            fontSize: typeof fontSize === "number" ? fontSize : undefined,
            fontFamily: resolvedFontFamily,
            flex: 1,
            padding: 0,
            textAlign: (style as any)?.textAlign || "left",
          },
          style as any,
        ]}
        placeholderTextColor={placeholderTextColor}
        maxLength={maxLength}
        // React Native accessibility props
        testID={dataTestId || testID || id}
        accessibilityLabel={ariaLabel}
        accessibilityHint={ariaDescribedBy}
        accessibilityState={{
          disabled: disabled || ariaDisabled,
        }}
        accessible={true}
      />
    );
  }
);

InputPrimitive.displayName = "InputPrimitive";
